import { useTheme } from '@mui/material';
import { useEffect, useRef, useState, memo } from 'react';
import { useInlineAsset } from '../../context';
import { useIframeContent } from '../../../../../../../../hooks/useIframeContent';

// Helper function to decode HTML entities (e.g., &lt; to <, &gt; to >, etc.)
const decodeHTMLEntities = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

const InlineTwitterDisplay = memo((asset: any) => {
  const theme = useTheme();
  const { nodeID, inlineProps } = useInlineAsset();
  const [iframeHeight, setIframeHeight] = useState<number | undefined>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Decode escaped HTML entities before passing to useIframeContent
  const rawHtml = inlineProps.node.attrs?.html;
  const decodedHtml = rawHtml ? decodeHTMLEntities(rawHtml) : rawHtml;
  const { content, useSrcAttribute } = useIframeContent(decodedHtml);

  useEffect(() => {
    const loadContent = () => {
      const onLoad = () => {
        try {
          // Try to access content height, but handle potential cross-origin issues
          if (iframeRef.current?.contentWindow?.document?.body) {
            const scrollHeight =
              iframeRef.current.contentWindow.document.body.scrollHeight;
            if (scrollHeight) {
              setIframeHeight(scrollHeight + 50);
            }
          }
        } catch (error) {
          // If we get a security error, set a default reasonable height
          console.warn('Cross-origin restrictions prevented height calculation:', error);
          setIframeHeight(500); // Default height for cross-origin content
        }
      };

      if (iframeRef.current) {
        iframeRef.current.addEventListener('load', onLoad);
        return () => {
          iframeRef.current?.removeEventListener('load', onLoad);
        };
      }
    };

    loadContent();
  }, [content]);

  if (!inlineProps.node.attrs?.html || !content) {
    return null;
  }

  return (
    <iframe
      title={nodeID}
      ref={iframeRef}
      style={{
        width: '100%',
        height: iframeHeight || '300px', // Increased default height
        border: 'none',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
      }}
      loading="lazy"
      referrerPolicy="no-referrer"
      // Expanded sandbox permissions to allow nested iframes to work properly
      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
      allowFullScreen={false}
      {...(useSrcAttribute ? { src: content } : { srcDoc: content })}
    />
  );
});

export default InlineTwitterDisplay;
